import { z } from 'zod';
import { amountMinorSchema, isoTimestampSchema, uuidSchema } from './common';

export const billLineSchema = z.object({
  id: uuidSchema,
  order_item_id: uuidSchema,
  description: z.string(),
  quantity: z.number().int(),
  unit_price_minor: amountMinorSchema,
  line_total_minor: amountMinorSchema,
});

export const paymentSchema = z.object({
  id: uuidSchema,
  /**
   * `platform` = collected by the delivery platform (no cash, no change); `online` = paid by the
   * guest through a payment provider (ADR-0023).
   */
  method: z.enum(['cash', 'platform', 'online']),
  amount_minor: amountMinorSchema,
  tendered_minor: amountMinorSchema,
  change_minor: amountMinorSchema,
  /** Staff member who took the payment; null for online payments (confirmed by the provider). */
  received_by: uuidSchema.nullable(),
  received_at: isoTimestampSchema,
});
export type Payment = z.infer<typeof paymentSchema>;

export const billSchema = z.object({
  id: uuidSchema,
  bill_number: z.string(),
  table_session_id: uuidSchema.nullable(),
  order_type: z.enum(['dine_in', 'takeaway', 'delivery']),
  channel: z.enum(['walk_in', 'gofood', 'grabfood', 'shopeefood', 'other']),
  external_ref: z.string().nullable(),
  customer_name: z.string().nullable(),
  order_id: uuidSchema,
  /** Dine-in only. */
  table_name: z.string().nullable(),
  status: z.enum(['open', 'settled', 'void']),
  currency: z.string(),
  lines: z.array(billLineSchema),
  subtotal_minor: amountMinorSchema,
  service_charge_minor: amountMinorSchema,
  tax_minor: amountMinorSchema,
  rounding_minor: amountMinorSchema,
  total_minor: amountMinorSchema,
  paid_minor: amountMinorSchema,
  outstanding_minor: amountMinorSchema,
  service_charge_bps: z.number().int(),
  tax_rate_bps: z.number().int(),
  payments: z.array(paymentSchema),
  /**
   * Guest online payments that need staff attention: `pending` (the guest is paying right now —
   * don't collect twice) and `refund_required` (paid at the provider, but the bill could not
   * take it). Settled ones appear in `payments`.
   */
  online_payments: z.array(
    z.object({
      id: uuidSchema,
      status: z.enum(['pending', 'refund_required']),
      amount_minor: amountMinorSchema,
      created_at: isoTimestampSchema,
      failure_reason: z.string().nullable(),
    }),
  ),
  version: z.number().int(),
  created_at: isoTimestampSchema,
  settled_at: isoTimestampSchema.nullable(),
});
export type Bill = z.infer<typeof billSchema>;

export const billListResponseSchema = z.object({ items: z.array(billSchema) });

/** Creates the session's bill, or refreshes the open unpaid bill with newly submitted items. */
/** Dine-in: by table session. Takeaway/delivery: by order. Exactly one. */
export const createBillRequestSchema = z
  .object({ table_session_id: uuidSchema.optional(), order_id: uuidSchema.optional() })
  .refine((v) => (v.table_session_id === undefined) !== (v.order_id === undefined), {
    message: 'Provide either table_session_id or order_id.',
    path: ['order_id'],
  });

export const createPaymentRequestSchema = z.discriminatedUnion('method', [
  z.object({
    bill_id: uuidSchema,
    method: z.literal('cash'),
    /** Cash handed over by the guest; change is computed by the server. */
    tendered_minor: amountMinorSchema.refine((v) => v > 0, 'Must be positive'),
  }),
  z.object({
    bill_id: uuidSchema,
    /** Delivery orders only: the platform collected the full outstanding amount. */
    method: z.literal('platform'),
  }),
]);

export const paymentResultSchema = z.object({
  payment: paymentSchema,
  bill: billSchema,
});

export const cashShiftSchema = z.object({
  id: uuidSchema,
  status: z.enum(['open', 'closed']),
  user_id: uuidSchema,
  user_name: z.string(),
  currency: z.string(),
  opening_float_minor: amountMinorSchema,
  cash_sales_minor: amountMinorSchema,
  pay_in_minor: amountMinorSchema,
  pay_out_minor: amountMinorSchema,
  /** opening float + cash sales + pay-ins − pay-outs. */
  expected_cash_minor: amountMinorSchema,
  counted_cash_minor: amountMinorSchema.nullable(),
  /** counted − expected (negative = short). */
  discrepancy_minor: amountMinorSchema.nullable(),
  payments_count: z.number().int(),
  notes: z.string().nullable(),
  opened_at: isoTimestampSchema,
  closed_at: isoTimestampSchema.nullable(),
  version: z.number().int(),
});
export type CashShift = z.infer<typeof cashShiftSchema>;

export const currentCashShiftResponseSchema = z.object({ shift: cashShiftSchema.nullable() });
export const cashShiftListResponseSchema = z.object({ items: z.array(cashShiftSchema) });

export const openCashShiftRequestSchema = z.object({
  opening_float_minor: amountMinorSchema.refine((v) => v >= 0, 'Cannot be negative'),
});

export const cashMovementRequestSchema = z.object({
  type: z.enum(['pay_in', 'pay_out']),
  amount_minor: amountMinorSchema.refine((v) => v > 0, 'Must be positive'),
  reason: z.string().trim().min(1).max(200),
});

export const closeCashShiftRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  counted_cash_minor: amountMinorSchema.refine((v) => v >= 0, 'Cannot be negative'),
  notes: z.string().trim().max(500).nullable().default(null),
});
